import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { X, Camera, CheckCircle, SkipForward, Sparkle, Warning } from "@phosphor-icons/react";

/**
 * NameplateSweepModal — reusable catch-up walkthrough for any (address, unit).
 * Works at move-in, move-out, or any random Tuesday you realise you never
 * cataloged Unit 7B. Auto-populates address+unit; user just aims the phone
 * at each appliance data-plate. Claude 4.5 vision does the OCR, we auto-save
 * the asset with make/model/serial.
 *
 * Usage:
 *   <NameplateSweepModal address={address} unit={unit}
 *     onClose={() => setOpen(false)} onDone={(assets) => refetch()}/>
 */

// Order matters — we walk kitchen → laundry → mechanical → hvac last.
const SLOTS = [
  { key: "refrigerator", label: "Refrigerator", hint: "Plate is on the inside wall or ceiling of the fridge compartment" },
  { key: "range",        label: "Range / Oven", hint: "Plate is on the door jamb, behind the storage drawer, or on the back panel" },
  { key: "microwave",    label: "Microwave",    hint: "Plate is on the door frame or the top interior" },
  { key: "dishwasher",   label: "Dishwasher",   hint: "Plate is on the door edge or side jamb (open the door)" },
  { key: "disposal",     label: "Garbage Disposal", hint: "Plate is on the side of the unit under the sink" },
  { key: "water_heater", label: "Water Heater", hint: "Big yellow/silver plate on the top or side of the tank" },
  { key: "washer",       label: "Washer",       hint: "Plate on the back or inside the lid ring" },
  { key: "dryer",        label: "Dryer",        hint: "Plate on the back or inside the door frame" },
  { key: "hvac",         label: "HVAC / Furnace", hint: "Air handler side panel, furnace door interior, or condenser (outside)" },
  { key: "other",        label: "Other",        hint: "Booster pumps, sump, humidifier — anything else with a plate" },
];

const SLOT_KEY_TO_TYPE_LABEL = {
  refrigerator: "Refrigerator",
  range: "Range",
  microwave: "Microwave",
  dishwasher: "Dishwasher",
  disposal: "Garbage Disposal",
  water_heater: "Water Heater",
  washer: "Washer",
  dryer: "Dryer",
  hvac: "HVAC",
  other: "",
};

export default function NameplateSweepModal({ address, unit, onClose, onDone }) {
  const [existing, setExisting] = useState([]);       // assets already on this unit
  const [slots, setSlots] = useState({});             // {slotKey: {status, asset, parsed}}
  const [active, setActive] = useState(null);         // slot key currently in capture flow
  const [scanBusy, setScanBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [parsed, setParsed] = useState(null);         // { photo_path, make, type, model, serial, install_date, notes }
  const [locationText, setLocationText] = useState("");
  const fileRef = useRef(null);
  const createdThisSession = useRef([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/property-assets", { params: { address } });
        const rows = (r.data?.assets || []).filter(a => (a.unit || "").toLowerCase() === (unit || "").toLowerCase());
        setExisting(rows);
      } catch { /* ignore */ }
    })();
  }, [address, unit]);

  // Pre-mark slots as "done" if this unit already has an asset matching that type
  useEffect(() => {
    const next = {};
    existing.forEach(a => {
      const t = (a.type || a.category || "").toLowerCase();
      const match = SLOTS.find(s =>
        t.includes(s.key.replace("_", " ")) ||
        (s.key === "range" && (t.includes("stove") || t.includes("oven"))) ||
        (s.key === "hvac" && (t.includes("furnace") || t.includes("condenser") || t.includes("air handler"))) ||
        (s.key === "water_heater" && t.includes("water heater")) ||
        (s.key === "disposal" && t.includes("disposal"))
      );
      if (match && !next[match.key]) {
        next[match.key] = { status: "existing", asset: a };
      }
    });
    setSlots(next);
  }, [existing]);

  const openSlot = (key) => {
    setActive(key);
    setParsed(null);
    setLocationText("");
    // Trigger the file picker with camera preferred on mobile
    setTimeout(() => fileRef.current?.click(), 50);
  };

  const skipSlot = (key) => {
    setSlots({ ...slots, [key]: { status: "skipped" }});
    setActive(null);
    setParsed(null);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !active) return;
    setScanBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/property-assets/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      // Prefer a friendly `type` fallback based on slot when vision doesn't return one
      if (!data.type && SLOT_KEY_TO_TYPE_LABEL[active]) data.type = SLOT_KEY_TO_TYPE_LABEL[active];
      setParsed(data);
      if (!data.make && !data.model && !data.serial) {
        toast.warning("Plate not readable — you can still save the photo & type the plate manually below");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "Scan failed — try again with a closer, sharper photo"));
      setActive(null);
    } finally {
      setScanBusy(false);
    }
  };

  const saveAsset = async () => {
    if (!parsed || !active) return;
    setSaveBusy(true);
    try {
      const body = {
        address,
        unit,
        name: (parsed.type || SLOT_KEY_TO_TYPE_LABEL[active] || "Asset").trim(),
        type: (parsed.type || SLOT_KEY_TO_TYPE_LABEL[active] || "").trim(),
        make: (parsed.make || "").trim(),
        model: (parsed.model || "").trim(),
        serial: (parsed.serial || "").trim(),
        install_date: (parsed.install_date || "").trim(),
        notes: (parsed.notes || "").trim(),
        photo_path: parsed.photo_path,
        location: locationText.trim(),
      };
      const { data: asset } = await api.post("/property-assets", body);
      createdThisSession.current.push(asset);
      setSlots({ ...slots, [active]: { status: "captured", asset } });
      toast.success(`${body.name} saved`);
      // Advance to next un-touched slot
      const remaining = SLOTS.filter(s => !slots[s.key] || slots[s.key].status === "skipped").filter(s => s.key !== active);
      const next = remaining[0];
      setActive(null);
      setParsed(null);
      setLocationText("");
      if (next) setTimeout(() => openSlot(next.key), 300);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally {
      setSaveBusy(false);
    }
  };

  const cancelActive = () => {
    setActive(null);
    setParsed(null);
    setLocationText("");
  };

  const done = SLOTS.filter(s => slots[s.key]?.status === "captured" || slots[s.key]?.status === "existing").length;
  const total = SLOTS.length;

  const handleClose = () => {
    if (createdThisSession.current.length && onDone) onDone(createdThisSession.current);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={handleClose} data-testid="nameplate-sweep-modal">
      <div className="bg-white w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto border-2 border-slate-900 shadow-[6px_6px_0_#0f172a]"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="p-3 bg-slate-900 text-white flex items-center gap-2">
          <Sparkle weight="fill" size={16}/>
          <div className="min-w-0 flex-1">
            <div className="font-display font-black text-sm uppercase tracking-widest">Nameplate Sweep</div>
            <div className="text-[10px] opacity-70 truncate">{address} · Unit {unit}</div>
          </div>
          <div className="text-[10px] font-black uppercase tracking-widest bg-emerald-500 text-slate-900 px-2 py-1"
            data-testid="sweep-progress">
            {done} / {total}
          </div>
          <button onClick={handleClose} data-testid="sweep-close-btn"
            className="h-8 w-8 hover:bg-white/10 flex items-center justify-center">
            <X size={16}/>
          </button>
        </div>

        {/* Capture flow */}
        {active && (
          <div className="p-4 bg-emerald-50 border-b-2 border-emerald-500" data-testid="sweep-capture-panel">
            <div className="flex items-center gap-2 mb-2">
              <Camera weight="fill" size={16} className="text-emerald-600"/>
              <div className="font-display font-black text-sm uppercase tracking-widest">
                Capturing: {SLOTS.find(s => s.key === active)?.label}
              </div>
              <button onClick={cancelActive} className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-600 hover:text-slate-900">Cancel</button>
            </div>
            <div className="text-xs text-slate-600 mb-2 italic">
              💡 {SLOTS.find(s => s.key === active)?.hint}
            </div>

            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={handleFile} data-testid="sweep-file-input"/>

            {scanBusy && (
              <div className="p-4 bg-white border-2 border-emerald-300 text-center">
                <Sparkle weight="fill" size={24} className="text-emerald-600 mx-auto animate-pulse"/>
                <div className="mt-2 text-sm font-black uppercase tracking-widest text-emerald-700">
                  Reading the plate…
                </div>
              </div>
            )}

            {!scanBusy && !parsed && (
              <button onClick={() => fileRef.current?.click()}
                data-testid="sweep-camera-btn"
                className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 shadow-[3px_3px_0_#0f172a]">
                <Camera weight="fill" size={20}/> Take photo of data-plate
              </button>
            )}

            {!scanBusy && parsed && (
              <div className="space-y-2" data-testid="sweep-review-panel">
                {parsed.photo_path && (
                  <a href={fileUrl(parsed.photo_path)} target="_blank" rel="noopener noreferrer">
                    <img src={fileUrl(parsed.photo_path)} alt=""
                      className="w-full max-h-40 object-contain bg-slate-100 border-2 border-slate-300"/>
                  </a>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Type" value={parsed.type} onChange={v => setParsed({ ...parsed, type: v })}
                    testid="sweep-type"/>
                  <Field label="Make" value={parsed.make} onChange={v => setParsed({ ...parsed, make: v })}
                    testid="sweep-make"/>
                  <Field label="Model" value={parsed.model} onChange={v => setParsed({ ...parsed, model: v })}
                    testid="sweep-model"/>
                  <Field label="Serial" value={parsed.serial} onChange={v => setParsed({ ...parsed, serial: v })}
                    testid="sweep-serial"/>
                </div>
                <Field label="Location (e.g. 'Kitchen', 'Utility closet')" value={locationText}
                  onChange={setLocationText} testid="sweep-location"/>
                <Field label="Install / manufacture date" value={parsed.install_date}
                  onChange={v => setParsed({ ...parsed, install_date: v })} testid="sweep-install"/>
                {parsed.notes && (
                  <div>
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-0.5">Notes</div>
                    <textarea rows={2} value={parsed.notes}
                      onChange={e => setParsed({ ...parsed, notes: e.target.value })}
                      className="w-full px-2 py-1 border-2 border-slate-300 text-xs"/>
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <button onClick={() => fileRef.current?.click()}
                    data-testid="sweep-retake-btn"
                    className="h-10 px-3 border-2 border-slate-900 hover:bg-slate-100 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
                    <Camera size={12}/> Retake
                  </button>
                  <button onClick={saveAsset} disabled={saveBusy}
                    data-testid="sweep-save-btn"
                    className="h-10 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] disabled:opacity-50">
                    {saveBusy ? "Saving…" : "Save & continue"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Slot grid */}
        <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="sweep-slot-grid">
          {SLOTS.map(s => {
            const state = slots[s.key];
            const status = state?.status;
            const isDone = status === "captured" || status === "existing";
            const isSkipped = status === "skipped";
            return (
              <div key={s.key} data-testid={`sweep-slot-${s.key}`}
                className={`p-2 border-2 flex flex-col gap-1 ${
                  isDone ? "border-emerald-500 bg-emerald-50" :
                  isSkipped ? "border-slate-300 bg-slate-100 opacity-60" :
                  active === s.key ? "border-emerald-600 bg-white ring-2 ring-emerald-400" :
                  "border-slate-300 bg-white hover:border-slate-900"
                }`}>
                <div className="flex items-center gap-1">
                  <div className="font-black text-xs uppercase tracking-widest flex-1">{s.label}</div>
                  {isDone && <CheckCircle weight="fill" size={16} className="text-emerald-600"/>}
                  {isSkipped && <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Skipped</span>}
                </div>
                {isDone && state?.asset && (
                  <div className="text-[10px] text-slate-600 truncate">
                    {state.asset.make || "?"} · {state.asset.model || "—"}
                  </div>
                )}
                {!isDone && !isSkipped && (
                  <div className="flex gap-1 mt-auto">
                    <button onClick={() => openSlot(s.key)}
                      data-testid={`sweep-open-${s.key}`}
                      className="h-8 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center justify-center gap-1">
                      <Camera size={11}/> Snap
                    </button>
                    <button onClick={() => skipSlot(s.key)}
                      data-testid={`sweep-skip-${s.key}`}
                      className="h-8 px-2 border-2 border-slate-400 hover:bg-slate-100 font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1 text-slate-600">
                      <SkipForward size={10}/>
                    </button>
                  </div>
                )}
                {isDone && (
                  <button onClick={() => openSlot(s.key)}
                    data-testid={`sweep-recapture-${s.key}`}
                    className="mt-auto h-7 px-2 border border-slate-400 hover:bg-white font-black uppercase tracking-widest text-[9px] text-slate-600">
                    Add another
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="p-3 border-t-2 border-slate-200 flex items-center gap-2 flex-wrap">
          <div className="text-[10px] text-slate-500 flex-1 min-w-[200px]">
            💡 Tip: Skip is fine — you can always come back later. Every capture auto-attaches to Unit <b>{unit}</b>.
          </div>
          <button onClick={handleClose} data-testid="sweep-finish-btn"
            className="h-10 px-4 bg-slate-900 hover:bg-black text-white font-black uppercase tracking-widest text-[10px]">
            Done for now
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-0.5">{label}</div>
      <input value={value || ""} onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-9 px-2 border-2 border-slate-300 text-sm font-mono"/>
    </div>
  );
}
